import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { Role } from '@app/shared/models/enums';
import { LoggedUserModel } from './logged-user.model';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { ASSOCIATIONS_STORE_KEY } from '@features/associations/store/associations-store-key';
import { rememberReturnUrl } from './return-url';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private oidcSecurityService = inject(OidcSecurityService);

  /** Set while a sign-in is under way — see {@link signIn}. */
  private isSigningIn = false;

  constructor() {
    this.loggedUserSubject = new BehaviorSubject(this.loggedUser);
  }

  loggedUserSubject: BehaviorSubject<LoggedUserModel | null>;
  loggedUser: LoggedUserModel | null = null;

  /**
   * Settles the session — processes a login callback if the URL carries one — and completes
   * @remarks Awaited by an app initializer in `app.config.ts`, and that is the point: the router
   * starts after app initializers, so every guard and every component that asks who is signed in
   * gets an answer that exists. When this ran from `AppComponent.ngOnInit` instead, the router
   * was already navigating while `checkAuth()` was still in flight, and each place that asked too
   * early read the not-signed-in answer: `/` was redirected to signin, signin saw no user and
   * bounced to `/authentication/unauthorized`, and that page ran `checkAuth()` a second time —
   * exchanging the same authorization code twice, which fails and sends the browser back to the
   * identity provider. Hence the redirects an administrator saw before landing anywhere.
   * @returns Observable that completes once the session is known
   */
  checkUser(): Observable<boolean> {
    return this.oidcSecurityService.checkAuth().pipe(
      tap(({ isAuthenticated }) => {
        // The redirect is over either way, so the sign-in guard has to open again here — a
        // page that comes back from bfcache keeps this singleton alive, and a guard left
        // closed would silently swallow every later sign-in for the rest of the tab's life.
        this.isSigningIn = false;

        if (isAuthenticated) {
          this.setUser();
        }
      }),
      map(({ isAuthenticated }) => isAuthenticated),
    );
  }

  /**
   * Whether a session is live, read from the state {@link checkUser} settled
   * @remarks Deliberately not `checkAuth()`: that call *processes the login callback*, so asking
   * it a second time while the authorization code is still in the URL redeems the code twice.
   * The second exchange fails, and the library answers a failed callback by navigating to
   * `unauthorizedRoute`.
   * @returns Observable of the current authentication state
   */
  isAuthenticated() {
    return this.oidcSecurityService.isAuthenticated$;
  }

  getAccessToken() {
    return this.oidcSecurityService.getAccessToken(); // getAccessToken now returns an Observable
  }

  getLoggedUser() {
    return this.loggedUserSubject.asObservable();
  }

  /**
   * Whether the signed-in session acts as SuperAdmin — the only role that may edit permissions
   * (#924, enforced by the API). Answers once, from the OIDC claims rather than `loggedUserSubject`:
   * the session is settled before the router starts (the app initializer awaits `checkUser()`), so
   * the first `userData$` value is the real answer, and no session answers "no" instead of hanging.
   */
  isSuperAdmin(): Observable<boolean> {
    return this.oidcSecurityService.userData$.pipe(
      take(1),
      map(({ userData }) => Number(userData?.primary_role) === Role.SuperAdmin),
    );
  }

  /**
   * Publishes the signed-in user, or `null` when there is nothing to publish
   * @remarks Truthy means a real user: signin navigates on that alone. This used to seed
   * `new LoggedUserModel()` — an empty object at runtime, and therefore truthy — before reading
   * the claims, so valid tokens with no stored `userData` published a user whose `primaryRole`
   * was `NaN`. Signin then routed them into the guarded area, where the guard read the empty
   * `userData$` and sent them off to sign in again.
   *
   * Each value is published from inside the subscription, so a token renewal that brings new
   * claims reaches subscribers instead of quietly mutating the object behind their backs.
   */
  setUser() {
    this.oidcSecurityService.userData$.subscribe(({ userData }) => {
      this.loggedUser =
        userData == null
          ? null
          : {
              id: Number(userData.sub),
              name: userData.given_name ?? '',
              initial: (userData.given_name ?? '').slice(0, 1),
              email: userData.email,
              primaryRole: Number(userData.primary_role),
              roles: userData.roles ?? [],
            };

      this.loggedUserSubject.next(this.loggedUser);
    });
  }

  /**
   * Starts the sign-in redirect, at most once
   * @remarks `authorize()` writes a fresh state and nonce each time it is called. Several
   * parallel requests can fail with 401 together — the dashboard alone opens two — and each one
   * asks to sign in; without this guard the last write would invalidate the redirect already
   * under way, and the callback would fail its state check.
   *
   * The guard is opened again by {@link checkUser} and {@link signOut}, so a redirect that
   * never completes — blocked, or backed out of — does not leave the tab unable to sign in.
   * @param returnUrl Where signin sends the user once the login is through; without one they
   * land on their role's landing route (see `return-url.ts`)
   * @returns Observable that completes immediately
   */
  signIn(returnUrl?: string): Observable<boolean> {
    if (this.isSigningIn) {
      return of(true);
    }

    this.isSigningIn = true;
    rememberReturnUrl(returnUrl);
    this.oidcSecurityService.authorize();

    return of(true);
  }

  signOut(): Observable<boolean> {
    this.clearLocalUser();
    this.oidcSecurityService.logoff().subscribe();

    return of(true);
  }

  /**
   * Drops this origin's token state without calling the auth server's end-session endpoint —
   * for the front-channel logout iframe, where the server has already ended the session.
   */
  signOutLocal(): void {
    this.clearLocalUser();
    this.oidcSecurityService.logoffLocal();
  }

  /**
   * The auth server's session id (`sid` claim of the id_token), or `null` when the token has
   * none — for the front-channel logout page, which only acts on the session named in its URL.
   */
  sessionId(): Observable<string | null> {
    return this.oidcSecurityService
      .getPayloadFromIdToken()
      .pipe(map((payload) => (typeof payload?.sid === 'string' ? payload.sid : null)));
  }

  /** Forgets the published user and reopens the sign-in guard (see {@link signIn}). */
  private clearLocalUser(): void {
    // The next user in this tab must not inherit the associations (and the one last viewed).
    sessionStorage.removeItem(ASSOCIATIONS_STORE_KEY);
    this.isSigningIn = false;
    this.loggedUser = null;
    this.loggedUserSubject.next(null);
  }
}
